import React from "react"
import { Box, Center, Text, VStack, HStack, Progress, Spinner, Skeleton } from "@chakra-ui/react"
import { FaWarehouse, FaUsers, FaCog, FaTruck, FaClipboard, FaGasPump } from "react-icons/fa"
import { IconType } from "react-icons"

export type LoadingPhase = 
  | 'mounting'
  | 'loading-module' 
  | 'initializing'
  | 'rendering'
  | 'ready'
  | 'error'
  | 'timeout'

export interface LoadingState {
  phase: LoadingPhase
  progress: number
  message: string
  startTime: number
  error?: Error
}

interface SectionLoadingDisplayProps {
  sectionName: string
  loadingState: LoadingState
  onRetry?: () => void
}

const SECTION_ICONS: Record<string, IconType> = {
  assets: FaWarehouse,
  customers: FaUsers,
  systems: FaCog,
  projects: FaTruck,
  orders: FaClipboard,
  filling: FaGasPump,
}

const PHASE_MESSAGES: Record<LoadingPhase, string> = {
  mounting: 'Preparing section...',
  'loading-module': 'Loading section code...',
  initializing: 'Setting up section...',
  rendering: 'Rendering interface...',
  ready: 'Section loaded successfully',
  error: 'Failed to load section',
  timeout: 'Section loading timed out',
}

const PHASE_PROGRESS: Record<LoadingPhase, number> = {
  mounting: 10,
  'loading-module': 30,
  initializing: 60,
  rendering: 80,
  ready: 100,
  error: 0,
  timeout: 0,
}

export const SectionLoadingDisplay: React.FC<SectionLoadingDisplayProps> = ({
  sectionName,
  loadingState,
  onRetry,
}) => {
  const { phase, progress, message, startTime } = loadingState
  const Icon = SECTION_ICONS[sectionName] || FaWarehouse
  const elapsedTime = Math.round((Date.now() - startTime) / 1000)
  
  const displayProgress = progress > 0 ? progress : PHASE_PROGRESS[phase]
  const displayMessage = message || PHASE_MESSAGES[phase]

  // Show skeleton for initial loading phases
  if (phase === 'mounting' || phase === 'loading-module') {
    return <SectionSkeleton sectionName={sectionName} />
  }

  return (
    <Center minH="400px" p={6}>
      <VStack gap={6} maxW="400px" w="100%">
        {/* Section Icon */}
        <Box color="blue.500" fontSize="4xl">
          <Icon />
        </Box>

        {/* Section Name */}
        <Text fontSize="xl" fontWeight="bold" textAlign="center">
          Loading {sectionName.charAt(0).toUpperCase() + sectionName.slice(1)}
        </Text>

        {/* Progress Bar */}
        <Box w="100%">
          <Progress.Root 
            value={displayProgress} 
            colorPalette="blue"
            size="lg"
          >
            <Progress.Track bg="gray.100">
              <Progress.Range />
            </Progress.Track>
          </Progress.Root>
          <HStack justify="space-between" mt={2}>
            <Text fontSize="sm" color="gray.600">
              {displayMessage}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {displayProgress}%
            </Text>
          </HStack>
        </Box>

        {/* Loading Spinner for active phases */}
        {(phase === 'initializing' || phase === 'rendering') && (
          <Spinner size="lg" color="blue.500" />
        )}

        {/* Elapsed Time */}
        <Text fontSize="xs" color="gray.400">
          {elapsedTime}s elapsed
        </Text>

        {/* Phase Details */}
        <VStack gap={1} fontSize="sm" color="gray.500" textAlign="center">
          <Text>Phase: {phase.replace('-', ' ')}</Text>
          {phase === 'loading-module' && (
            <Text>Downloading section bundle...</Text>
          )}
          {phase === 'initializing' && (
            <Text>Setting up React components...</Text>
          )}
          {phase === 'rendering' && (
            <Text>Mounting section interface...</Text>
          )}
        </VStack>
      </VStack>
    </Center>
  )
}

export const SectionSkeleton: React.FC<{ sectionName: string }> = ({ sectionName }) => {
  return (
    <Box p={6} h="100%" minH="400px">
      <VStack gap={6} align="stretch">
        {/* Header skeleton */}
        <HStack justify="space-between" align="center">
          <HStack gap={3}>
            <Skeleton w="40px" h="40px" />
            <VStack align="start" gap={1}>
              <Skeleton h="24px" w="200px" />
              <Skeleton h="16px" w="120px" />
            </VStack>
          </HStack>
          <HStack gap={2}>
            <Skeleton w="80px" h="32px" />
            <Skeleton w="100px" h="32px" />
          </HStack>
        </HStack>

        {/* Navigation skeleton */}
        <HStack gap={4}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} h="32px" w="80px" />
          ))}
        </HStack>

        {/* Content area skeleton */}
        <Box flex={1}>
          <VStack gap={4} align="stretch">
            {/* Table/list skeleton */}
            <Box>
              <HStack justify="space-between" mb={4}>
                <Skeleton h="20px" w="150px" />
                <Skeleton h="32px" w="120px" />
              </HStack>
              
              {/* Table header */}
              <HStack gap={4} mb={2} p={3} bg="gray.50">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} h="16px" flex={1} />
                ))}
              </HStack>
              
              {/* Table rows */}
              {Array.from({ length: 8 }).map((_, i) => (
                <HStack key={i} gap={4} p={3} borderBottom="1px" borderColor="gray.100">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Skeleton key={j} h="16px" flex={1} />
                  ))}
                </HStack>
              ))}
            </Box>
          </VStack>
        </Box>

        {/* Footer skeleton */}
        <HStack justify="between" align="center">
          <Skeleton h="16px" w="100px" />
          <HStack gap={2}>
            <Skeleton w="32px" h="32px" />
            <Skeleton w="32px" h="32px" />
            <Skeleton w="32px" h="32px" />
          </HStack>
        </HStack>
      </VStack>
    </Box>
  )
}

// Loading state manager hook
export const useSectionLoadingState = (sectionName: string) => {
  const [loadingState, setLoadingState] = React.useState<LoadingState>({
    phase: 'mounting',
    progress: 0,
    message: '',
    startTime: Date.now(),
  })

  const updatePhase = React.useCallback((phase: LoadingPhase, message?: string, progress?: number) => {
    setLoadingState(prev => ({
      ...prev,
      phase,
      message: message || PHASE_MESSAGES[phase],
      progress: progress ?? PHASE_PROGRESS[phase],
    }))
  }, [])

  const setError = React.useCallback((error: Error) => {
    setLoadingState(prev => ({
      ...prev,
      phase: 'error',
      error,
      message: `Error: ${error.message}`,
      progress: 0,
    }))
  }, [])

  const setTimeout = React.useCallback(() => {
    setLoadingState(prev => ({
      ...prev,
      phase: 'timeout',
      message: 'Section loading timed out after 10 seconds',
      progress: 0,
    }))
  }, [])

  const reset = React.useCallback(() => {
    setLoadingState({
      phase: 'mounting',
      progress: 0,
      message: '',
      startTime: Date.now(),
    })
  }, [])

  const getElapsedTime = React.useCallback(() => {
    return Math.round((Date.now() - loadingState.startTime) / 1000)
  }, [loadingState.startTime])

  return {
    loadingState,
    updatePhase,
    setError,
    setTimeout,
    reset,
    getElapsedTime,
  }
}
